import {ObjectUtils, Logger, Utils, NumberUtils, StringUtils, CommonTypes, UriHelper} from 'neo-common';

const key_NumberFormat = 'NumberFormat';
const key_ForceToolEnabled = 'ForceToolEnabled';
const key_ForceToolDebugEnabled = 'ForceToolDebugEnabled';
const key_ForceToolDemoPINHash = 'ForceToolDemoPINHash';
const key_ExitUrl = 'ExitUrl';
const key_HistoryUrl = 'HistoryUrl';
const key_RealityCheckStartInterval = 'RealityCheckStartInterval';
const key_RealityCheckInterval = 'RealityCheckInterval';
const key_ServerRequestTimeout = 'ServerRequestTimeout';
const key_Workarounds = 'Workarounds';
const key_KeepAliveInterval = 'KeepAliveInterval';
const key_KeepAliveRefreshInterval = 'KeepAliveRefreshInterval';
const key_SplashScreen = 'SplashScreen';
const key_GameSkin = 'GameSkin';
const key_MaxButton = 'MaxButton';
const key_FRBFreeSpins = 'FRBFreeSpins';
const key_PlayerRetentionPlugins = 'PlayerRetentionPlugins';
const key_EventTrackerEnabledEvents = 'EventTrackerEnabledEvents';
const key_TournamentEventTrackerEnabled = 'TournamentEventTrackerEnabled';

export interface WorkaroundsType {
    [workaroundId: string]: CommonTypes.IBowserMinVersions;
}

export abstract class GameConfigurationBase {
    private _configuration: Object;

    constructor(configuration: Object) {
        if (DEBUG) {
            if (Utils.isNullOrUndefined(configuration)) {
                Logger.errorGroup('GameConfiguration', `Parameter "configuration" must not be null!`);
            }
        }

        this._configuration = Utils.valueOrDefault(configuration, {});
    }

    public getRaw(): Object {
        return this._configuration;
    }

    public merge(otherConfiguration: Object, fileName: string): void {
        if (!Utils.isNullOrUndefined(otherConfiguration)) {
            ObjectUtils.assign(this._configuration, otherConfiguration);

            if (DEBUG) {
                Logger.debugGroup('GameConfiguration',
                    `Game configuration merged with file '${fileName}', merge result: `, this._configuration);
            }
        }
    }

    private setValue<T>(key: string, value: T): void {
        if (Utils.isNullOrUndefined(this._configuration)) {
            this._configuration = {};
        }

        this._configuration[key] = value;

        if (DEBUG) {
            Logger.debugGroup('GameConfiguration', `Setting game configuration key '${key}' with value '${value}' using proxy.`);
        }
    }

    protected getValue<T>(key: string, defaultValue: T): T {
        let result = defaultValue;
        if (this._configuration && this._configuration.hasOwnProperty(key)) {
            result = this._configuration[key];
            if (!Utils.isNullOrUndefined(result)) {
                let resultValueType = typeof result;
                let expectedValueType = typeof defaultValue;
                let incompatibleTypes = resultValueType !== expectedValueType;

                if (incompatibleTypes) {
                    if (expectedValueType === 'number' && resultValueType === 'string'){
                        let parsedResult = NumberUtils.tryParseNumber(result);
                        if (parsedResult.isValid) {
                            result = parsedResult.value as any;
                            incompatibleTypes = false;
                        }
                    } else if (expectedValueType === 'boolean' && resultValueType === 'string' && !StringUtils.isNullOrEmpty(result as any)){
                        let bstr = (result as any).toLowerCase();
                        if (bstr === 'true') {
                            result = true as any;
                            incompatibleTypes = false;
                        } else if (bstr === 'false') {
                            result = false as any;
                            incompatibleTypes = false;
                        }
                    }

                    if (incompatibleTypes) {
                        if (DEBUG) {
                            Logger.errorGroup('GameConfiguration',
                                `Incompatible value type (${resultValueType}) of key "${key}" from configuration file, ` +
                                `expected value type (${expectedValueType}), returning default value '${defaultValue}'`);
                        }

                        result = defaultValue;
                    }
                }
            }
        }

        return result;
    }

    /**
     * @property NumberFormat - string mask how to format numbers, e.g. (0,00)
     */
    public get NumberFormat(): string {
        return this.getValue(key_NumberFormat, '0.00');
    }

    /**
     * @property ForceToolEnabled - flag if 'force tool' is enabled
     */
    public get ForceToolEnabled(): boolean {
        return this.getValue(key_ForceToolEnabled, false);
    }

    /**
     * @property ForceToolDebugEnabled - flag if 'force tool debug' is enabled
     */
    public get ForceToolDebugEnabled(): boolean {
        return this.getValue(key_ForceToolDebugEnabled, false);
    }

    /**
     * @property ForceToolDemoPINHash - Hash of PIN require to enter Force Tool DEMO
     */
    public get ForceToolDemoPINHash(): string {
        // return this.getValue(key_ForceToolDemoPINHash, '');
        return '';
    }

    /**
     * Gets exit url (if supported by game and/or server)
     */
    public get ExitUrl(): string {
        return this.getValue(key_ExitUrl, '');
    }

    /**
     * Gets history url (if supported by game and/or server)
     */
    public get HistoryUrl(): string {
        return this.getValue(key_HistoryUrl, '');
    }

    /**
     * @property SplashScreen
     * @returns {boolean}
     */
    public get SplashScreen(): boolean {
        return this.getValue(key_SplashScreen, true);
    }

    /**
     * Gets number of seconds for start of reality check to initial show of dialog.
     * This value is used for 1st show of reality check dialog.
     */
    public get RealityCheckStartInterval(): number {
        return this.getValue(key_RealityCheckStartInterval, -1);
    }

    /**
     * Gets number of seconds of reccuring interval when to show reality check dialog.
     * This value is used on 2nd and all other shows of reality check dialog.
     */
    public get RealityCheckInterval(): number {
        return this.getValue(key_RealityCheckInterval, -1);
    }

    /**
     * Gets number of miliseconds of server requests timeout time
     */
    public get ServerRequestTimeout(): number {
        return this.getValue(key_ServerRequestTimeout, -1);
    }

    /**
     * Gets workarounds definitions for specific browsers/versions, eg:
     * {
     *   'preloader_audio': {
     *      chrome: '61'
     *   }
     * }
     */
    public get Workarounds(): WorkaroundsType {
        return this.getValue(key_Workarounds, null);
    }

    public getWorkaround(workaroundId: string): CommonTypes.IBowserMinVersions {
        let w = this.Workarounds;
        return ObjectUtils.isNullOrEmpty(w) ? null : w[workaroundId];
    }

    /**
     * Gets number of seconds of alive state interval.
     */
    public get KeepAliveInterval(): number {
        return this.getValue(key_KeepAliveInterval, 8 * 60);
    }

    /**
     * Gets number of seconds of reccuring keep alive interval.
     */
    public get KeepAliveRefreshInterval(): number {
        return this.getValue(key_KeepAliveRefreshInterval, 60);
    }

    /**
     * @property MaxButton
     * @constructor
     */
    public get MaxButton(): boolean {
        return this.getValue(key_MaxButton, false);
    }

    /**
     * @property FRBFreeSpins
     * @constructor
     */
    public get FRBFreeSpins(): boolean {
        return this.getValue(key_FRBFreeSpins, false);
    }

    /**
     * @property GameSkin
     * @returns {string}
     */
    public get GameSkin(): string {
        return this.getValue(key_GameSkin, '');
    }

    /**
     * @property EventTrackerEnabledEvents
     * @constructor
     */
    public get EventTrackerEnabledEvents(): string {
        return this.getValue(key_EventTrackerEnabledEvents, '');
    }

    /**
     * @property TournamentEventTrackerEnabled
     * @constructor
     */
    public get TournamentEventTrackerEnabled(): boolean {
        return this.getValue(key_TournamentEventTrackerEnabled, false);
    }

    /**
     * @property PlayerRetentionPlugins
     * @constructor
     */
    public get PlayerRetentionPlugins(): Array<string> {
        // return this.getValue(key_PlayerRetentionPlugins, []);

        //ToDo temporary
        let data = this.getValue(key_PlayerRetentionPlugins, []);
        if (data == null || data.length === 0) {
            const uriData = UriHelper.parseUrl(window.location.href, true);
            const pluginsData = uriData.query['plugins'];
            if (pluginsData != null) {
                data = pluginsData.split('|');
            }
        }

        return data;
    }
}


/** WEBPACK FOOTER **
 ** ./Engine/GameEngine/GameConfigurationBase.ts
 **/